import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Database, Download, Trash2, PlayCircle } from "lucide-react";

const formatBytes = (n) => {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0, v = n;
  while (v >= 1024 && i < 3) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
};

const AdminBackups = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/backups");
      setItems(data);
    } catch (e) { toast.error("Failed to load", { description: e.response?.data?.detail || "" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const trigger = async () => {
    setCreating(true);
    try {
      const note = window.prompt("Backup note (optional):", "");
      await api.post("/admin/backups", { note: note || "" });
      toast.success("Backup triggered");
      load();
    } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
    finally { setCreating(false); }
  };

  const download = (b) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/${b.id}/download`;
    const token = localStorage.getItem("dh_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${b.name}.gz`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => toast.error("Download failed"));
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete backup "${b.name}"? File will be removed from disk.`)) return;
    try { await api.delete(`/admin/backups/${b.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
  };

  return (
    <AdminLayout title="Database backups" actions={
      <Button data-testid="backup-create-btn" onClick={trigger} disabled={creating} className="rounded-none bg-accent hover:bg-accent/90">
        <PlayCircle size={14} className="mr-2" /> {creating ? "Running…" : "Backup now"}
      </Button>
    }>
      <div className="text-sm text-muted-foreground mb-4 border border-border bg-card p-4">
        <div className="font-medium flex items-center gap-2 mb-2"><Database size={14} /> Backup Manager</div>
        Backups are saved as gzipped mongodump archives under <code>/app/backups/</code>. Scheduled backups + cloud storage (S3 / R2 / B2) ship in Phase 5.5.
      </div>
      <div className="border border-border bg-card">
        {loading && <div className="p-8 text-center text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && <div className="p-12 text-center text-sm text-muted-foreground" data-testid="backups-empty">No backups yet. Trigger your first one.</div>}
        {!loading && items.map((b) => (
          <div key={b.id} data-testid={`backup-row-${b.id}`} className="p-4 border-b border-border last:border-b-0 grid grid-cols-1 md:grid-cols-[1fr_120px_120px_140px_200px] gap-3 items-center">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{b.name}</div>
              {b.note && <div className="text-xs text-muted-foreground italic truncate">&ldquo;{b.note}&rdquo;</div>}
              {b.error && <div className="text-xs text-destructive truncate">{b.error}</div>}
            </div>
            <div className="text-xs">{formatBytes(b.size)}</div>
            <div className="text-xs uppercase">{b.status}</div>
            <div className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleString()}</div>
            <div className="flex gap-2 justify-end">
              {b.status === "success" && <button data-testid={`backup-download-${b.id}`} onClick={() => download(b)} className="text-[11px] px-2 py-1 border border-border hover:border-accent flex items-center gap-1"><Download size={10} /> Download</button>}
              <button data-testid={`backup-delete-${b.id}`} onClick={() => remove(b)} className="text-[11px] px-2 py-1 border border-border hover:border-destructive text-destructive flex items-center gap-1"><Trash2 size={10} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
};

export default AdminBackups;
